import { useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchSession } from "../lib/api-client";
import { useAuth } from "../contexts/AuthContext";
import { useTheme, type ThemeMode } from "../contexts/ThemeContext";
import NavSection from "./NavSection";

interface SidebarNavigationProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

function SidebarNavigation({ isOpen = true, onToggle }: SidebarNavigationProps): JSX.Element {
  const { teamId, formId } = useParams<{ teamId: string; formId: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [showTeamSwitcher, setShowTeamSwitcher] = useState(false);

  const sessionQuery = useQuery({
    queryKey: ["session"],
    queryFn: fetchSession,
  });

  const memberships = sessionQuery.data?.session?.memberships ?? [];
  const currentTeam = memberships.find((m) => m.teamId === teamId);
  const session = sessionQuery.data?.session;

  const handleTeamSwitch = (newTeamId: string) => {
    setShowTeamSwitcher(false);
    navigate(`/teams/${newTeamId}`);
  };

  const canAccessSettings = (membership?: typeof memberships[0]) => {
    if (!membership) return false;
    return (
      membership.role === "TeamAdmin" ||
      membership.role === "SuperAdmin" ||
      membership.roles?.includes("TeamAdmin")
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="sidebar-overlay"
          onClick={onToggle}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 998,
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar ${isOpen ? "sidebar--open" : ""}`}
        style={{
          width: "240px",
          height: "100vh",
          position: "sticky",
          top: 0,
          background: "var(--color-surface)",
          borderRight: "1px solid var(--color-border)",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          zIndex: 999,
        }}
      >
        {/* Logo/Brand */}
        <div
          style={{
            padding: "1.5rem 1.25rem",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              width: "100%",
              textAlign: "left",
            }}
          >
            <h1
              style={{
                fontSize: "1.25rem",
                fontWeight: 700,
                margin: 0,
                color: "var(--color-primary)",
              }}
            >
              RWA Forms
            </h1>
            <p
              style={{
                fontSize: "0.75rem",
                color: "var(--color-text-muted)",
                margin: "0.25rem 0 0",
              }}
            >
              Form Builder
            </p>
          </button>
        </div>

        {/* Team Switcher */}
        {memberships.length > 0 && (
          <div
            style={{
              padding: "1rem 1.25rem",
              borderBottom: "1px solid var(--color-border)",
            }}
          >
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="button ghost"
                onClick={() => setShowTeamSwitcher(!showTeamSwitcher)}
                style={{
                  width: "100%",
                  justifyContent: "space-between",
                  padding: "0.625rem 0.875rem",
                  fontSize: "0.875rem",
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {currentTeam?.teamName || "Select Team"}
                </span>
                <span style={{ fontSize: "0.75rem" }}>▼</span>
              </button>

              {showTeamSwitcher && (
                <>
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 9,
                    }}
                    onClick={() => setShowTeamSwitcher(false)}
                  />
                  <div
                    className="animate-fade-in"
                    style={{
                      position: "absolute",
                      top: "calc(100% + 0.5rem)",
                      left: 0,
                      right: 0,
                      background: "var(--color-surface)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-base)",
                      boxShadow: "var(--shadow-lg)",
                      padding: "0.5rem",
                      zIndex: 10,
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    {memberships.map((membership) => (
                      <button
                        key={membership.teamId}
                        type="button"
                        onClick={() => handleTeamSwitch(membership.teamId)}
                        style={{
                          width: "100%",
                          padding: "0.625rem 0.875rem",
                          textAlign: "left",
                          background:
                            membership.teamId === teamId
                              ? "var(--color-primary-soft)"
                              : "transparent",
                          border: "none",
                          borderRadius: "calc(var(--radius-base) - 2px)",
                          cursor: "pointer",
                          fontSize: "0.875rem",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => {
                          if (membership.teamId !== teamId) {
                            e.currentTarget.style.background = "var(--color-bg-solid)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (membership.teamId !== teamId) {
                            e.currentTarget.style.background = "transparent";
                          }
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{membership.teamName}</div>
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "var(--color-text-muted)",
                            marginTop: "0.125rem",
                          }}
                        >
                          {membership.role}
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <nav
          style={{
            flex: 1,
            padding: "1rem 0.75rem",
            overflowY: "auto",
          }}
        >
          {/* Main Navigation */}
          <NavLink
            to="/"
            className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              padding: "0.75rem 1rem",
              borderRadius: "calc(var(--radius-base) - 2px)",
              fontSize: "0.9rem",
              fontWeight: 500,
              color: "var(--color-text)",
              textDecoration: "none",
              transition: "all 0.15s",
            }}
          >
            <span style={{ fontSize: "1.125rem" }}>📋</span>
            <span>Forms</span>
          </NavLink>

          {/* Form Management Section */}
          {teamId && formId && (
            <NavSection title="Form" icon="📝" defaultOpen={true}>
              <NavLink
                to={`/teams/${teamId}/forms/${formId}/analytics`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>📊</span>
                <span>Analytics</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/forms/${formId}/responses`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>💬</span>
                <span>Responses</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/forms/${formId}/versions`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>📦</span>
                <span>Versions</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/forms/${formId}/sharing`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>🔗</span>
                <span>Sharing</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/forms/${formId}/files`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>📁</span>
                <span>Files</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/forms/${formId}/theme`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>🎨</span>
                <span>Theme</span>
              </NavLink>
            </NavSection>
          )}

          {/* Team Management Section */}
          {teamId && (
            <NavSection title="Team" icon="🏢" defaultOpen={false}>
              <NavLink
                to={`/teams/${teamId}/sharing`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>🔗</span>
                <span>Share Forms</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/members`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>👥</span>
                <span>Members</span>
              </NavLink>

              {canAccessSettings(currentTeam) && (
                <NavLink
                  to={`/teams/${teamId}/invitations`}
                  className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "calc(var(--radius-base) - 2px)",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    color: "var(--color-text)",
                    textDecoration: "none",
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: "1.125rem" }}>📧</span>
                  <span>Invitations</span>
                </NavLink>
              )}

              {canAccessSettings(currentTeam) && (
                <NavLink
                  to={`/teams/${teamId}/settings`}
                  className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                    padding: "0.75rem 1rem",
                    borderRadius: "calc(var(--radius-base) - 2px)",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    color: "var(--color-text)",
                    textDecoration: "none",
                    transition: "all 0.15s",
                  }}
                >
                  <span style={{ fontSize: "1.125rem" }}>⚙️</span>
                  <span>Settings</span>
                </NavLink>
              )}

              <NavLink
                to={`/teams/${teamId}/themes`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>🎨</span>
                <span>Branding</span>
              </NavLink>

              <NavLink
                to={`/teams/${teamId}/domains`}
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>🌐</span>
                <span>Domains</span>
              </NavLink>
            </NavSection>
          )}

          {/* Admin Section */}
          {session?.roles.includes("SuperAdmin") && (
            <NavSection title="Administration" icon="🔐" defaultOpen={false}>
              <NavLink
                to="/admin/teams"
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>🏢</span>
                <span>All Teams</span>
              </NavLink>

              <NavLink
                to="/admin/users"
                className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  padding: "0.75rem 1rem",
                  borderRadius: "calc(var(--radius-base) - 2px)",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: "var(--color-text)",
                  textDecoration: "none",
                  transition: "all 0.15s",
                }}
              >
                <span style={{ fontSize: "1.125rem" }}>👥</span>
                <span>Users</span>
              </NavLink>
            </NavSection>
          )}

          <hr style={{ margin: "0.75rem 0", border: "none", borderTop: "1px solid var(--color-border)" }} />

          {/* Account Section */}
          <NavSection title="Account" icon="👤" defaultOpen={false}>
            <NavLink
              to="/account"
              className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.75rem 1rem",
                borderRadius: "calc(var(--radius-base) - 2px)",
                fontSize: "0.9rem",
                fontWeight: 500,
                color: "var(--color-text)",
                textDecoration: "none",
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: "1.125rem" }}>⚙️</span>
              <span>Settings</span>
            </NavLink>

            <NavLink
              to="/privacy"
              className={({ isActive }) => `nav-link ${isActive ? "nav-link--active" : ""}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                padding: "0.75rem 1rem",
                borderRadius: "calc(var(--radius-base) - 2px)",
                fontSize: "0.9rem",
                fontWeight: 500,
                color: "var(--color-text)",
                textDecoration: "none",
                transition: "all 0.15s",
              }}
            >
              <span style={{ fontSize: "1.125rem" }}>🛡️</span>
              <span>Privacy & GDPR</span>
            </NavLink>
          </NavSection>
        </nav>

        {/* User Profile */}
        <div
          style={{
            padding: "1rem 1.25rem",
            borderTop: "1px solid var(--color-border)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
              marginBottom: "0.75rem",
            }}
          >
            <div
              style={{
                width: "2rem",
                height: "2rem",
                borderRadius: "50%",
                background: "var(--color-primary)",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "0.875rem",
                flexShrink: 0,
              }}
            >
              {session?.email?.charAt(0).toUpperCase() || "U"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {session?.email}
              </div>
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--color-text-muted)",
                }}
              >
                {session?.roles.join(", ") || "User"}
              </div>
            </div>
          </div>

          {/* Theme Toggle */}
          <ThemeToggle />

          <button
            type="button"
            className="button ghost"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            style={{
              width: "100%",
              fontSize: "0.875rem",
              padding: "0.5rem 0.875rem",
              color: "var(--color-critical)",
              marginTop: "0.5rem",
            }}
          >
            Sign Out
          </button>
        </div>
      </aside>
    </>
  );
}

/**
 * Theme toggle component for switching between Light/Dark/System modes
 */
function ThemeToggle(): JSX.Element {
  const { mode, setMode } = useTheme();

  const options: { value: ThemeMode; label: string; icon: string }[] = [
    { value: "light", label: "Light", icon: "☀️" },
    { value: "dark", label: "Dark", icon: "🌙" },
    { value: "system", label: "System", icon: "💻" },
  ];

  return (
    <div style={{ marginBottom: "0.5rem" }}>
      <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", marginBottom: "0.35rem" }}>
        Theme
      </div>
      <div style={{ display: "flex", gap: "0.25rem" }}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setMode(option.value)}
            style={{
              flex: 1,
              padding: "0.35rem 0.4rem",
              fontSize: "0.75rem",
              background: mode === option.value ? "var(--color-primary-soft)" : "transparent",
              color: mode === option.value ? "var(--color-primary)" : "var(--color-text)",
              border: mode === option.value ? "1px solid var(--color-primary)" : "1px solid var(--color-border)",
              borderRadius: "6px",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
            title={option.label}
          >
            {option.icon}
          </button>
        ))}
      </div>
    </div>
  );
}

export default SidebarNavigation;
